import moment from "moment";
import { alpha } from "@mui/material/styles";
import {
  AppointmentDto,
  EventTimeSlotDto,
  LeaveOfAbsenceDto,
  WorkingHoursDto,
  WorkingHoursDtoSourceEnum,
} from "../../../../../api-client";
import { mobiliTheme } from "../../../../../themes/mobiliTheme";
import {
  adaptDerivedLunchBreak,
  clientLunchBreakToCalendarEvent,
  adaptPublicHolidayFromApi,
  clientPublicHolidayToCalendarEvent,
} from "../api-to-client/specialized.adapter";
import { CalendarEvent, ClientUser } from "../types";
import {
  adaptAppointmentsToCalendarEvents,
  adaptTimeSlotsToCalendarEvents,
} from "../batch.utilities";

export const workingHoursToCalendarEvents = (workingHours: WorkingHoursDto): CalendarEvent | null => {
  const resourceId = workingHours.userId;

  // console.debug("[workingHoursToCalendarEvents]", {
    // id: workingHours.slotId,
    // resourceId,
    // start: moment(workingHours.start).toISOString(),
    // end: moment(workingHours.end).toISOString(),
    // source: workingHours.source,
  // });

  // Render cancelled full-day as background blocked slot (dark/unavailable appearance)
  if (workingHours.source === 'cancelled-fullDay') {
    // console.log("[workingHoursToCalendarEvents] Rendering cancelled full day as blocked", workingHours);
    return {
      id: `cancelled-${workingHours.userId}-${workingHours.date}`,
      title: "Tag abgesagt",
      start: moment(workingHours.start).toISOString(),
      end: moment(workingHours.end).toISOString(),
      resourceId,
      editable: false,
      display: 'background', // Renders as background to block the time
      backgroundColor: 'rgba(0, 48, 89, 0.5)', // Same as fc-non-business (non-working hours)
      classNames: ["cancelled-day"],
      extendedProps: {
        eventType: "working-hours",
        timeSlotType: "CancelledDay",
        user: { id: workingHours.userId } as ClientUser,
        isContinuous: false,
        isException: true,
        exceptionId: workingHours.exceptionId,
      },
    };
  }

  return {
    id: workingHours.slotId??`wh-${workingHours.userId}-${workingHours.start}`,
    title: "Arbeitszeit",
    start: moment(workingHours.start).toISOString(),
    end: moment(workingHours.end).toISOString(),
    editable: true, // Explicitly enable editing (resizing)
    durationEditable: true,
    // Ensure resourceId is a string (FullCalendar requirement for consistent diffing)
    resourceId,
    resizable: true,
    classNames: ["lunchbreak"],

    // TODO we could have a different color for exceptions
    color: workingHours.exceptionId
      ? alpha(mobiliTheme.palette.success.main, 0.9)
      : alpha(mobiliTheme.palette.success.light, 0.9),

    // All custom properties go in extendedProps
    extendedProps: {
      eventType: "working-hours",
      //   timeSlotId: workingHours.id, // TODO is it ok to have this not provided !?!
      timeSlotType: "WorkingHours",
      slotId: workingHours.slotId, // Original API slotId (may be undefined)

      user: { id: workingHours.userId } as ClientUser,
      isContinuous: false,
      isException: workingHours.exceptionId !== undefined,
      exceptionId: workingHours.exceptionId,
    },
  };
};

/**
 * An absence as the timesheet needs it: the API DTO, or the same fields with
 * Date instances (what callers that pre-convert hand over). `id` and `loAType`
 * matter — the absence is rendered from them (MD-507).
 */
export type TimeSheetAbsence = Pick<LeaveOfAbsenceDto, "id" | "loAType" | "user"> & {
  start?: string | Date;
  end?: string | Date;
};

/**
 * Helper function to check if a timeslot overlaps with any Leave of Absence for the same user
 */
const overlapsWithLoAInTimeSheet = (
  timeSlot: { start?: Date; end?: Date; user?: { id: string }; userId?: string },
  leaveOfAbsences: TimeSheetAbsence[]
): boolean => {
  // A derived lunch break carries `userId`, a time slot `user.id` — reading
  // only the latter made this filter a no-op for breaks (MD-507).
  const userId = timeSlot.user?.id ?? timeSlot.userId;
  if (!timeSlot.start || !timeSlot.end || !userId) {
    return false;
  }

  return leaveOfAbsences.some(loa => {
    if (!loa.start || !loa.end || loa.user?.id !== userId) {
      return false;
    }
    // Check for overlap: timeSlot starts before LoA ends AND timeSlot ends after LoA starts
    return timeSlot.start! < new Date(loa.end) && timeSlot.end! > new Date(loa.start);
  });
};

export const timeSheetToCalendarEvents = ({
  workingHours,
  appointments,
  eventTimeSlots,
  leaveOfAbsences = [],
}: {
  workingHours: WorkingHoursDto[];
  appointments: AppointmentDto[];
  eventTimeSlots: EventTimeSlotDto[];
  /** The day's absences as the API returns them — id and loAType included. */
  leaveOfAbsences?: TimeSheetAbsence[];
}): CalendarEvent[] => {
  // Separate public holidays from working hours
  const publicHolidays = workingHours?.filter((wh) => wh.source === WorkingHoursDtoSourceEnum.PublicHoliday) || [];
  const publicHolidayEvents = publicHolidays
    .map((ph) => adaptPublicHolidayFromApi(ph))
    .map((ph) => clientPublicHolidayToCalendarEvent(ph));

  // Process lunch breaks
  const lunchBreaks = workingHours?.filter((wh) => wh.isBreak && wh.source !== WorkingHoursDtoSourceEnum.PublicHoliday) || [];
  // Filter out lunch breaks that overlap with Leave of Absences
  const lunchBreakEvents = lunchBreaks
    .map((lb) => adaptDerivedLunchBreak(lb))
    .filter((ts) => !overlapsWithLoAInTimeSheet(ts, leaveOfAbsences))
    .map((ts) => clientLunchBreakToCalendarEvent(ts));

  const workingHoursEvents = workingHours
    .filter((wh) => !wh.isBreak && wh.source !== WorkingHoursDtoSourceEnum.PublicHoliday)
    .map((wh) => workingHoursToCalendarEvents(wh))
    .filter((event): event is CalendarEvent => event !== null); // Filter out null values from cancelled days

  const appointmentEvents = adaptAppointmentsToCalendarEvents(
    (appointments as AppointmentDto[]) || [],
    {
      isBackgroundEvent: true, // Show appointments in background (faded) in TimeSheet mode
    }
  );

  // Pass leaveOfAbsences to adaptTimeSlotsToCalendarEvents for EventTimeSlot
  // filtering — the absences THEMSELVES are emitted separately below, so the
  // background copy this call would also produce is dropped.
  const timeslotEvents = adaptTimeSlotsToCalendarEvents(
    { eventTimeSlots, leaveOfAbsences },
    { isBackgroundEvent: true }
  ).filter((event) => event.extendedProps?.timeSlotType !== "LeaveOfAbsence");

  // MD-507: the absences are emitted from the DTOs themselves, so they keep
  // their id and their type as the title ("Urlaub", "Krank") — the copy
  // dropped above arrived stripped of both, which is why a vacation day looked
  // like an ordinary working day and the report was "the absence does not show
  // up in Arbeitszeiten". They render as a background block covering the time
  // they span. Display-only in this view: the timesheet's change handler knows
  // working hours and breaks, nothing else — a dragged absence would be
  // "Unknown event type" with the block left where it was dropped. Absences
  // are edited in the day view (AbsenceDialog), as before.
  const absenceEvents = adaptTimeSlotsToCalendarEvents({ leaveOfAbsences }).map((event) => ({
    ...event,
    editable: false,
    durationEditable: false,
    resizable: false,
    display: "background", // Render as background to block the time
  }));

  return [
    ...lunchBreakEvents,
    ...workingHoursEvents,
    ...publicHolidayEvents,
    ...appointmentEvents,
    ...timeslotEvents,
    ...absenceEvents,
  ];
};
